import { useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '@/store/authStore';
import { toast } from 'sonner';
import { setAppSocket } from '@/services/socketRegistry';

/**
 * Connessione Socket.IO autenticata: notifiche server (assegnazioni, ecc.) + toast + Notification API (Windows/macOS).
 */
export function useSocketNotifications() {
  const { isAuthenticated, user } = useAuthStore();
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const token = localStorage.getItem('accessToken');
    if (!token) return;

    const socket = io({
      path: '/socket.io',
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 8,
      reconnectionDelay: 2000,
    });

    socket.on('connect_error', (err) => {
      console.warn('[socket] connect_error', err.message);
    });

    socket.on('settings:maintenance', () => {
      window.dispatchEvent(new Event('settings:changed'));
    });

    socket.on('staff:message', (payload: { conversationId: string; message: { senderId: string; body?: string } }) => {
      window.dispatchEvent(new Event('staff-messaging:invalidate'));
      if (!payload?.message || payload.message.senderId === user.id) return;

      const params = new URLSearchParams(window.location.search);
      const onThread =
        window.location.pathname === '/messaggi' &&
        params.get('conversationId') === payload.conversationId;
      const quiet =
        document.visibilityState === 'visible' && document.hasFocus() && onThread;
      if (quiet) return;

      const bodyPreview = (payload.message.body || '').slice(0, 160);
      const showNotification = () => {
        try {
          const n = new Notification('Messaggio interno', {
            body: bodyPreview || undefined,
            icon: `${window.location.origin}/favicon.ico`,
          });
          n.onclick = () => {
            window.dispatchEvent(
              new CustomEvent('staff-messaging:navigate', {
                detail: { conversationId: payload.conversationId },
              }),
            );
            n.close();
          };
        } catch {
          /* ignore */
        }
      };

      if (document.visibilityState === 'hidden' || !document.hasFocus()) {
        if (typeof window !== 'undefined' && 'Notification' in window) {
          if (Notification.permission === 'granted') showNotification();
          else if (Notification.permission === 'default') {
            void Notification.requestPermission().then((p) => {
              if (p === 'granted') showNotification();
            });
          }
        }
      } else {
        toast.info(bodyPreview || 'Nuovo messaggio interno');
      }
    });

    socket.on('staff:messageDelivered', () => {
      window.dispatchEvent(new Event('staff-messaging:invalidate'));
    });
    socket.on('staff:messageDeleted', () => {
      window.dispatchEvent(new Event('staff-messaging:invalidate'));
    });
    socket.on('staff:conversationUpdated', () => {
      window.dispatchEvent(new Event('staff-messaging:invalidate'));
    });

    socket.on('notification', (payload: { title?: string; message?: string }) => {
      const title = payload?.title || 'Notifica';
      const body = payload?.message || '';
      toast.info(body ? `${title}: ${body}` : title);

      if (typeof window === 'undefined' || !('Notification' in window)) return;

      const show = () => {
        try {
          new Notification(title, {
            body: body || undefined,
            icon: `${window.location.origin}/favicon.ico`,
            silent: false,
          });
        } catch {
          /* ignore */
        }
      };

      if (Notification.permission === 'granted') {
        show();
      } else if (Notification.permission === 'default') {
        void Notification.requestPermission().then((p) => {
          if (p === 'granted') show();
        });
      }
    });

    socketRef.current = socket;
    setAppSocket(socket);

    return () => {
      setAppSocket(null);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [isAuthenticated, user?.id]);
}
